/**
 * Central workspace account switch handshake for web + extension embedded app.
 */
import api from '@/api';
import {
  clearClientActiveAid,
  getClientActiveAid,
  isExtensionEmbeddedApp,
  setClientActiveAid,
} from '@trove-shared/workspace/activeAid';
import { useSessionsStore } from '@/stores/sessions-store';
import { reloadSessionsAfterAccountSwitch } from '@/hooks/useLoadSessions';

const ACCOUNT_SWITCH_RELOAD_SKIP_MS = 2000;
let accountSwitchReloadSkipUntil = 0;

/** Skip duplicate reload when web already reloaded after initiating switch. */
export function shouldSkipAccountSwitchBroadcastReload(): boolean {
  return Date.now() < accountSwitchReloadSkipUntil;
}

function markAccountSwitchReloadDone(): void {
  accountSwitchReloadSkipUntil = Date.now() + ACCOUNT_SWITCH_RELOAD_SKIP_MS;
}

export type ExtensionAccountSwitchResult = {
  success: boolean;
  error?: string;
};

/** Send confirmed aid to extension service worker (embedded app only). */
export async function sendAccountSwitchedToExtension(
  aid: string
): Promise<ExtensionAccountSwitchResult> {
  if (!isExtensionEmbeddedApp()) {
    return { success: true };
  }

  const runtime = typeof window !== 'undefined' ? window.chrome?.runtime : undefined;
  if (!runtime?.sendMessage) {
    return { success: true };
  }

  const attempt = (): Promise<ExtensionAccountSwitchResult> =>
    new Promise(resolve => {
      try {
        runtime.sendMessage(
          { action: 'ACCOUNT_SWITCHED', data: { aid } },
          response => {
            const lastError = runtime.lastError as { message?: string } | undefined;
            if (lastError?.message) {
              resolve({ success: false, error: lastError.message });
              return;
            }
            const body = response as { success?: boolean; error?: string } | undefined;
            if (body?.success === false) {
              resolve({
                success: false,
                error:
                  typeof body.error === 'string'
                    ? body.error
                    : 'Extension account switch failed',
              });
              return;
            }
            resolve({ success: true });
          }
        );
      } catch (err) {
        resolve({
          success: false,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    });

  let result = await attempt();
  if (!result.success) {
    await new Promise(r => setTimeout(r, 500));
    result = await attempt();
  }
  if (!result.success) {
    throw new Error(
      result.error ??
        'Could not sync the extension with the new workspace. Try reloading the extension.'
    );
  }
  return result;
}

export type NotifyAccountSwitchOptions = {
  /** Refreshes AuthContext user from GET /preference when provided. */
  refreshUserPreference?: () => Promise<void>;
};

/**
 * Sync client state after local workspace switch (this device only).
 */
export async function notifyAccountSwitch(
  newAid: string,
  options: NotifyAccountSwitchOptions = {}
): Promise<void> {
  await setClientActiveAid(newAid);
  await sendAccountSwitchedToExtension(newAid);

  if (options.refreshUserPreference) {
    await options.refreshUserPreference();
  } else {
    await api.get('/preference');
  }

  useSessionsStore.getState().resetForAccountSwitch();
  await reloadSessionsAfterAccountSwitch();
  markAccountSwitchReloadDone();
}

/** Use workspace on THIS device only — does not mutate server default. */
export async function switchWorkspaceAccount(
  aid: string,
  options: NotifyAccountSwitchOptions = {}
): Promise<void> {
  await notifyAccountSwitch(aid, options);
}

/** Set server default workspace for new devices — does not switch this device. */
export async function setDefaultWorkspaceAccount(aid: string): Promise<void> {
  const response = await api.put('/users/accounts/switch', { aid });
  if (response.data?.status !== 'success') {
    throw new Error(
      typeof response.data?.message === 'string'
        ? response.data.message
        : 'Could not set default workspace'
    );
  }
}

/** Switch this device and optionally set server default (checkbox flow). */
export async function switchAndSetDefaultWorkspaceAccount(
  aid: string,
  options: NotifyAccountSwitchOptions = {}
): Promise<void> {
  await switchWorkspaceAccount(aid, options);
  await setDefaultWorkspaceAccount(aid);
}

/** Seed local aid from preference when unset (first login on device). */
export function seedClientActiveAidFromPreference(pref: {
  aid?: string;
  defaultAid?: string;
}): string | null {
  const existing = getClientActiveAid();
  if (existing) return existing;
  const seed =
    typeof pref.defaultAid === 'string' && pref.defaultAid
      ? pref.defaultAid
      : typeof pref.aid === 'string' && pref.aid
        ? pref.aid
        : null;
  if (seed) {
    void setClientActiveAid(seed);
    return seed;
  }
  return null;
}

function sendAccountLogoutToExtension(): Promise<{ success?: boolean }> {
  return new Promise(resolve => {
    const runtime = typeof window !== 'undefined' ? window.chrome?.runtime : undefined;
    if (!runtime?.sendMessage) {
      resolve({ success: true });
      return;
    }
    try {
      runtime.sendMessage({ action: 'ACCOUNT_LOGOUT' }, response => {
        void runtime.lastError;
        resolve((response as { success?: boolean }) ?? { success: true });
      });
    } catch {
      resolve({ success: true });
    }
  });
}

/** Clears client session state when the Firebase user was removed (last workspace deleted). */
export async function notifyAccountLogout(): Promise<void> {
  clearClientActiveAid();
  useSessionsStore.getState().resetForAccountSwitch();
  await sendAccountLogoutToExtension();
}
